import { StudyState } from "@app/types";
import { Review } from "@core/domain/models/review.model";
import { computed, ref, Ref } from "vue";

export type PdfFilterType = "all" | "has" | "none";

export default () => {
  const filters = ref<{ [key in StudyState]: boolean }>({
    [StudyState.DUPLICATE]: true,
    [StudyState.EXCLUDED]: true,
    [StudyState.FAVOURITE]: true,
    [StudyState.INCLUDED]: true,
    [StudyState.UNSCREENED]: true,
    [StudyState.QUARANTINED]: true,
  });

  const query = ref<string>("");

  const highlightOnly = ref(false);

  const sourceFilter: Ref<string[]> = ref([]);
  const hasPdfFilter: Ref<PdfFilterType> = ref("all");
  const titleFilter = ref<string>("");
  const authorFilter = ref<string>("");
  const hasScreeningSuggestionFilter = ref<boolean>(false);
  const hasExtractionSuggestionFilter = ref<boolean>(false);

  const terms = computed<string[]>(() => {
    return query.value
      .split(";")
      .map((st) =>
        st
          .trim()
          .toLowerCase()
          .replaceAll(/[\\.+*?^$[\](){}/'#:=!|]/gi, "\\$&"),
      )
      .filter((st) => !!st);
  });

  const hasFiltersApplied = computed(() => {
    return (
      Object.values(filters.value).some((isApplied) => !isApplied) ||
      sourceFilter.value.length > 0 ||
      hasPdfFilter.value !== "all" ||
      titleFilter.value.length > 0 ||
      authorFilter.value.length > 0 ||
      hasScreeningSuggestionFilter.value ||
      hasExtractionSuggestionFilter.value
    );
  });

  function toggleStateFilter(filterName: StudyState, value: boolean | null = null) {
    filters.value[filterName] = value ?? !filters.value[filterName];
  }

  function toggleAllStateFilters() {
    const isEveryFilterActive = Object.keys(filters.value).every(
      (k) => filters.value[k as StudyState],
    );
    Object.keys(filters.value).forEach((filter) =>
      toggleStateFilter(filter as StudyState, !isEveryFilterActive),
    );
  }

  function filterStudiesByState(studySate: StudyState) {
    filters.value[studySate] = true;
    Object.keys(filters.value).forEach((filter) => {
      if (filter !== studySate) filters.value[filter as StudyState] = false;
    });
  }

  function resetFilters() {
    Object.keys(filters.value).forEach((filter) => {
      filters.value[filter as StudyState] = true;
    });
    sourceFilter.value = [];
    hasPdfFilter.value = "all";
    titleFilter.value = "";
    authorFilter.value = "";
    hasScreeningSuggestionFilter.value = false;
    hasExtractionSuggestionFilter.value = false;
  }

  function findSearchSource(review: Review, searchName: string): string {
    const searchIndex = parseInt(searchName.replace("search ", "")) - 1;
    return review.searches?.[searchIndex]?.source.name ?? "";
  }

  function findSearchQuery(review: Review, searchName: string): string {
    const searchIndex = parseInt(searchName.replace("search ", "")) - 1;
    const search = review.searches?.[searchIndex];
    return search?.searchQuery ?? "";
  }

  return {
    toggleAllStateFilters,
    toggleStateFilter,
    filterStudiesByState,
    resetFilters,
    filters,
    terms,
    query,
    highlightOnly,
    sourceFilter,
    hasPdfFilter,
    titleFilter,
    authorFilter,
    hasScreeningSuggestionFilter,
    hasExtractionSuggestionFilter,
    findSearchSource,
    findSearchQuery,
    hasFiltersApplied,
  };
};
